import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { acquireBodyScrollLock } from './body-scroll-lock';

interface PortalProps {
  children: React.ReactNode;
  /** Lock document.body scroll while this portal is mounted. */
  lockBody?: boolean;
}

type PortalParent = {
  contains: (node: Node) => boolean;
  removeChild: (node: Node) => unknown;
};

export function removePortalElement(parent: PortalParent, el: Node) {
  if (parent.contains(el)) {
    parent.removeChild(el);
  }
}

/**
 * Renders children into a portal appended to document.body.
 * Body scroll locking is opt-in so passive portals (toasts, menus) do not
 * accidentally freeze the page.
 */
export function Portal({ children, lockBody = false }: PortalProps) {
  const elRef = useRef<HTMLDivElement | null>(null);

  if (!elRef.current) {
    elRef.current = document.createElement('div');
  }

  useEffect(() => {
    const el = elRef.current!;
    document.body.appendChild(el);
    const releaseBodyLock = lockBody ? acquireBodyScrollLock() : undefined;

    return () => {
      removePortalElement(document.body, el);
      releaseBodyLock?.();
    };
  }, [lockBody]);

  return createPortal(children, elRef.current);
}

export type { PortalProps };
